/**
 * usePanelFocusListener - Helper hook for panels to listen to focus events
 */

import { useEffect } from 'react';
import type {
  PanelEventEmitter,
  PanelFocusEventPayload,
  PanelBlurEventPayload,
} from '@principal-ade/panel-framework-core';

/**
 * Hook for panels to listen to focus events from the framework
 *
 * This is a convenience hook that panels can use to respond to focus changes
 * from keyboard shortcuts, mouse clicks, or programmatic navigation.
 *
 * @param panelId - The ID of this panel (from panel metadata)
 * @param events - Event emitter from PanelComponentProps
 * @param onFocus - Callback when this panel receives focus
 * @param onBlur - Optional callback when this panel loses focus
 *
 * @example
 * ```typescript
 * function TerminalPanel({ context, actions, events }: PanelComponentProps) {
 *   const xtermRef = useRef<Terminal>();
 *
 *   usePanelFocusListener(
 *     'terminal',
 *     events,
 *     () => xtermRef.current?.focus(),
 *     () => xtermRef.current?.blur()
 *   );
 *
 *   return <div id="xterm-container" />;
 * }
 * ```
 */
export function usePanelFocusListener(
  panelId: string,
  events: PanelEventEmitter,
  onFocus: () => void,
  onBlur?: () => void
): void {
  useEffect(() => {
    // Listen for focus events
    const unsubscribeFocus = events.on<PanelFocusEventPayload>(
      'panel:focus',
      (event) => {
        if (event.payload.panelId === panelId) {
          onFocus();
        }
      }
    );

    // Listen for blur events (optional)
    let unsubscribeBlur: (() => void) | undefined;
    if (onBlur) {
      unsubscribeBlur = events.on<PanelBlurEventPayload>(
        'panel:blur',
        (event) => {
          if (event.payload.panelId === panelId) {
            onBlur();
          }
        }
      );
    }

    return () => {
      unsubscribeFocus();
      unsubscribeBlur?.();
    };
  }, [panelId, events, onFocus, onBlur]);
}
